import { useState, useEffect } from "react";
import api from "../services/api";

function pct(v) { return Math.min(Math.round(v || 0), 100); }
function fmtD(d) { return d ? new Date(d).toLocaleDateString("fr-FR") : "—"; }

function StatusBadge({ completeness }) {
  if (completeness >= 90) return <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Complet</span>;
  if (completeness >= 40) return <span style={{ color: "#d97706", fontWeight: 700 }}>🔄 En cours</span>;
  return <span style={{ color: "#9ca3af", fontWeight: 700 }}>⏳ À démarrer</span>;
}

function CompletenessBar({ value }) {
  const v = pct(value);
  const color = v >= 80 ? "#16a34a" : v >= 40 ? "#d97706" : "#e24b4a";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
      <div style={{ flex: 1, height: 6, background: "#f3f4f6", borderRadius: 4, overflow: "hidden" }}>
        <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 4 }} />
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color, minWidth: 32 }}>{v}%</span>
    </div>
  );
}

export default function MigrationDashboard({ onNavigate, currentCompanyId, companies }) {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(false);
  const [mergers, setMergers]   = useState([]);
  const [newMerger, setNewMerger] = useState({ source_id: "", target_id: "", merge_date: "", notes: "" });
  const [showMergerForm, setSMF] = useState(false);
  const [error, setError]       = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([api.catchup.dashboard(), api.mergers.list()])
      .then(([d, m]) => { setData(d||[]); setMergers(m||[]); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const createMerger = async () => {
    if (!newMerger.source_id || !newMerger.target_id) { setError("Sélectionnez les deux sociétés."); return; }
    try {
      await api.mergers.create({ source_company_id: newMerger.source_id, target_company_id: newMerger.target_id, merge_date: newMerger.merge_date, notes: newMerger.notes });
      setSMF(false);
      setNewMerger({ source_id: "", target_id: "", merge_date: "", notes: "" });
      load();
    } catch (e) { setError(e.message); }
  };

  const totalSocieties  = data.length;
  const done            = data.filter(d => pct(d.completeness) >= 80).length;
  const inProgress      = data.filter(d => pct(d.completeness) > 0 && pct(d.completeness) < 80).length;
  const todo            = data.filter(d => pct(d.completeness) === 0).length;
  const avgCompleteness = data.length > 0 ? Math.round(data.reduce((s,d) => s + pct(d.completeness), 0) / data.length) : 0;

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Tableau de bord — Migration & Rattrapage</h2>
        <button className="btn primary" onClick={() => setSMF(true)}>+ Planifier une fusion</button>
      </div>

      {error && <div style={{ padding:"10px 14px", background:"#dc262610", border:"1px solid #dc262630", borderRadius:8, color:"#dc2626", marginBottom:12, fontSize:13 }}>{error}</div>}

      {/* KPIs */}
      <div className="kpi-grid k4" style={{ marginBottom: 16 }}>
        <div className="kpi"><div className="kpi-label">Sociétés suivies</div><div className="kpi-value">{totalSocieties}</div></div>
        <div className="kpi"><div className="kpi-label">Complètes (≥80%)</div><div className="kpi-value" style={{ color:"var(--g)" }}>{done}</div></div>
        <div className="kpi"><div className="kpi-label">En cours</div><div className="kpi-value" style={{ color:"var(--amber)" }}>{inProgress}</div></div>
        <div className="kpi"><div className="kpi-label">Complétude moyenne</div><div className="kpi-value">{avgCompleteness}%</div></div>
      </div>

      {/* Tableau sociétés */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-header"><span className="card-title">📊 État par société</span></div>
        {loading ? <div style={{ textAlign:"center", padding:30, color:"var(--text-muted)" }}>Chargement…</div> : (
          <div className="tbl-overflow">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Société</th>
                  <th>Statut</th>
                  <th>FEC importés</th>
                  <th>Rattrapage</th>
                  <th>Relevés bancaires</th>
                  <th>Complétude</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.map(row => {
                  const c = row.company;
                  const fecYears = [...new Set(row.fec_imports.map(f => f.year || (f.period_from||'').slice(0,4)).filter(Boolean))].sort().join(", ");
                  const catchupYear = row.catchup?.year;
                  const recon = row.staged_total > 0 ? `${row.staged_validated}/${row.staged_total} validés` : "Aucun";
                  return (
                    <tr key={c.id} style={{ opacity: c.archived ? 0.5 : 1 }}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{c.name}</div>
                        <div style={{ fontSize: 11, color:"var(--text-muted)" }}>{c.code}{c.archived ? " · Archivée" : ""}</div>
                      </td>
                      <td><StatusBadge completeness={row.completeness} /></td>
                      <td>{fecYears ? <span style={{ fontSize:12 }}>{fecYears}</span> : <span style={{ color:"var(--text-muted)", fontSize:12 }}>Aucun</span>}</td>
                      <td>{catchupYear ? <span style={{ fontSize:12 }}>Jan–{fmtD(row.catchup?.period_end)} {catchupYear}</span> : <span style={{ color:"var(--text-muted)", fontSize:12 }}>—</span>}</td>
                      <td style={{ fontSize:12 }}>{recon}</td>
                      <td><CompletenessBar value={row.completeness} /></td>
                      <td>
                        <div style={{ display:"flex", gap:4 }}>
                          <button className="btn btn-sm" onClick={() => onNavigate?.("rattrapage")} style={{ fontSize:11, padding:"3px 8px" }}>Rattrapage</button>
                          <button className="btn btn-sm" onClick={() => onNavigate?.("fec")} style={{ fontSize:11, padding:"3px 8px" }}>FEC</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Fusions planifiées */}
      {mergers.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">🔀 Fusions planifiées</span></div>
          <div className="tbl-overflow">
            <table className="tbl">
              <thead><tr><th>Société absorbée</th><th>→</th><th>Société absorbante</th><th>Date d'effet</th><th>Statut</th><th></th></tr></thead>
              <tbody>
                {mergers.map(m => (
                  <tr key={m.id}>
                    <td style={{ fontWeight:700 }}>{m.source_name}</td>
                    <td style={{ textAlign:"center", color:"var(--text-muted)" }}>→</td>
                    <td style={{ fontWeight:700 }}>{m.target_name}</td>
                    <td style={{ fontSize:12 }}>{fmtD(m.merge_date) || "—"}</td>
                    <td>
                      <span style={{ fontSize:11, padding:"2px 8px", borderRadius:10,
                        background: m.status==="completed" ? "#d1fae5" : m.status==="in_progress" ? "#fef3c7" : "#e0e7ff",
                        color:      m.status==="completed" ? "#065f46" : m.status==="in_progress" ? "#92400e" : "#3730a3",
                      }}>
                        {{planned:"Planifiée",in_progress:"En cours",completed:"Terminée",cancelled:"Annulée"}[m.status]||m.status}
                      </span>
                    </td>
                    <td>
                      {m.status !== "completed" && (
                        <button className="btn btn-sm primary" onClick={() => onNavigate?.("fusion")} style={{ fontSize:11, padding:"3px 8px" }}>Gérer</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal planifier fusion */}
      {showMergerForm && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }}>
          <div className="card" style={{ width:460, maxWidth:"95vw" }}>
            <div className="card-header"><span className="card-title">🔀 Planifier une fusion</span></div>
            <div className="form-group">
              <label className="form-label">Société absorbée (disparaît)</label>
              <select className="form-control" value={newMerger.source_id} onChange={e => setNewMerger(p => ({...p, source_id: e.target.value}))}>
                <option value="">— Sélectionner —</option>
                {companies?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Société absorbante (récupère tout)</label>
              <select className="form-control" value={newMerger.target_id} onChange={e => setNewMerger(p => ({...p, target_id: e.target.value}))}>
                <option value="">— Sélectionner —</option>
                {companies?.filter(c => String(c.id) !== String(newMerger.source_id)).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Date d'effet</label>
              <input className="form-control" type="date" value={newMerger.merge_date} onChange={e => setNewMerger(p => ({...p, merge_date: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Notes (optionnel)</label>
              <textarea className="form-control" value={newMerger.notes} onChange={e => setNewMerger(p => ({...p, notes: e.target.value}))} rows={2} />
            </div>
            <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:8 }}>
              <button className="btn" onClick={() => setSMF(false)}>Annuler</button>
              <button className="btn primary" onClick={createMerger}>Planifier la fusion</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
